import { useState, useEffect } from 'react';
import { DashboardData, DashboardFilters } from '@/types/dashboard';

export const useDashboardData = (filters: DashboardFilters) => {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            // TODO: Replace with actual API call
            // For now, we'll simulate the data structure
            const mockData: DashboardData = {
                metrics: {
                    activeUsers: Math.floor(Math.random() * 100) + 50,
                    threads: Math.floor(Math.random() * 500) + 200,
                    messages: Math.floor(Math.random() * 2000) + 1000,
                    inputTokens: Math.floor(Math.random() * 100000) + 50000,
                    outputTokens: Math.floor(Math.random() * 80000) + 40000,
                    cachedTokens: Math.floor(Math.random() * 20000) + 10000,
                    avgLLMTiming: Math.random() * 2 + 0.5,
                    minLLMTiming: Math.random() * 0.5 + 0.1,
                    maxLLMTiming: Math.random() * 5 + 2,
                    avgTtFT: Math.random() * 0.1 + 0.1,
                    llmModels: [
                        { model: 'gpt-4', count: Math.floor(Math.random() * 100) + 50 },
                        { model: 'gpt-3.5-turbo', count: Math.floor(Math.random() * 200) + 100 },
                        { model: 'claude-3', count: Math.floor(Math.random() * 80) + 30 },
                    ],
                    modelCosts: [
                        { model: 'gpt-4', cost: Math.random() * 500 + 200 },
                        { model: 'gpt-3.5-turbo', cost: Math.random() * 300 + 100 },
                        { model: 'claude-3', cost: Math.random() * 400 + 150 },
                    ],
                    toolCalls: [
                        { tool: 'web_search', count: Math.floor(Math.random() * 50) + 20 },
                        { tool: 'file_reader', count: Math.floor(Math.random() * 30) + 10 },
                        { tool: 'calculator', count: Math.floor(Math.random() * 40) + 15 },
                    ],
                },
                timeSeries: generateTimeSeriesData(filters.timeFrame),
            };

            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            setData(mockData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchDashboardData();
    }, [filters]);

    return { data, loading, error, refetch: () => fetchDashboardData() };
};

const generateTimeSeriesData = (timeFrame: string): any[] => {
    const now = new Date();
    const intervals = timeFrame === '1h' ? 12 : timeFrame === '24h' ? 24 : timeFrame === '7d' ? 7 : 30;
    const intervalMs = timeFrame === '1h' ? 5 * 60 * 1000 : timeFrame === '24h' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

    return Array.from({ length: intervals }, (_, i) => {
        const timestamp = new Date(now.getTime() - (intervals - i - 1) * intervalMs);
        return {
            timestamp: timestamp.toISOString(),
            activeUsers: Math.floor(Math.random() * 50) + 20,
            threads: Math.floor(Math.random() * 100) + 30,
            messages: Math.floor(Math.random() * 500) + 100,
            inputTokens: Math.floor(Math.random() * 10000) + 2000,
            outputTokens: Math.floor(Math.random() * 8000) + 1500,
            cachedTokens: Math.floor(Math.random() * 2000) + 500,
            avgLLMTiming: Math.random() * 2 + 0.5,
        };
    });
};
